// Legacy Twilio Conversations media helpers.
//
// Twilio Conversations is decommissioned. Pre-cutover MMS thumbnails were served on demand via the
// `/message-media` proxy (conversations-service `getMessageMedia`), which now returns a quiet 200
// "unavailable" — the underlying Twilio media is gone / the parent Firestore doc was removed, and the
// legacy `messages` docs never TTL out (verified 2026-07-13), so this would otherwise 404 forever.
// New in-house MMS are re-hosted on S3 and carry a real `file.url`.
//
// These helpers let the chat UI (a) never auto-load a `/message-media` proxy URL (even if one was
// persisted into `file.url`), and (b) render a placeholder for legacy media that can no longer be served.
//
// NOTE: the test mirrors this logic (src/store/__tests__/legacyMedia.test.js) rather than importing it,
// to avoid webpack-ESM / node:test-CJS interop — same pattern as conversationsV2-filters. Keep in sync.

// True when the URL is the legacy `/message-media` Twilio proxy (not a real S3/blob URL).
export function isProxyUrl(url) {
  return typeof url === 'string' && url.indexOf('/message-media') !== -1;
}

// Extract the `mediaSID` query param from a legacy `/message-media` proxy URL, or '' if not a proxy URL.
// Some pre-cutover message docs persisted the proxy URL into `file.url` with NO separate `mediaSid`
// field; recovering it here lets isLegacyExpiredMedia() fire so those messages get the placeholder
// instead of silently falling through to raw "[Photo] filename" text.
export function proxyMediaSid(url) {
  if (!isProxyUrl(url)) return '';
  try {
    const q = String(url).split('?')[1] || '';
    const m = q.match(/(?:^|&)mediaSID=([^&]+)/i);
    return m ? decodeURIComponent(m[1]) : '';
  } catch (_) { return ''; }
}

// The real (S3/blob) media URL for a file descriptor, or '' if absent or only a dead proxy URL.
export function realFileUrl(f) {
  if (!f) return '';
  const u = f.url || f.localUrl || '';
  return isProxyUrl(u) ? '' : u;
}

// The URL the chat should render for a message file descriptor, per the Media-URL Lifecycle Standard:
//  - private in-house MMS (has an `s3Key`) → the STABLE `/sms-media?key=…` re-sign route built from the
//    KEY. Never the persisted presigned `file.url` (it expires after 7 days → broken image). The backend
//    endpoint mints a fresh 5-min presigned URL and 302s to it on every load.
//  - a legacy `/message-media` Twilio proxy URL → '' (stripped → placeholder).
//  - a real public/blob URL (no s3Key) → as-is.
export function renderMediaUrl(f, apiBase) {
  if (!f) return '';
  const key = f.s3Key || f.S3Key;
  if (key) {
    const base = String(apiBase || '').replace(/\/$/, '');
    return `${base}/sms-media?key=${encodeURIComponent(key)}`;
  }
  return realFileUrl(f);
}

// The ONE file descriptor the chat bubble should render for a message.
//
// vue-advanced-chat 0.6.9 has no multi-file message model — it renders a single `message.file` — so a
// multi-item MMS has to pick one. Picking `files[0]` BLINDLY is what made a patient's photo read as
// lost (prod, Mint Dental, 2026-08-25): a two-photo MMS whose FIRST item failed its S3 re-host but
// whose SECOND succeeded rendered "📷 Photo no longer available", while the good photo sat in S3 and
// had already reached OD imaging. The failed item is a marker, not the message.
//
// So: prefer the first SERVABLE item (an `s3Key` to re-sign, or a real non-proxy URL), and fall back
// to the first item so a message whose items are ALL unservable still reaches the placeholder path
// unchanged. Returns null for an empty/absent list.
export function pickRenderableFile(list) {
  const arr = Array.isArray(list) ? list.filter(Boolean) : [];
  if (!arr.length) return null;
  return arr.find((f) => f.s3Key || f.S3Key || realFileUrl(f)) || arr[0];
}

// How many photos from ONE inbound MMS the chat will render.
//
// Product decision (Nilay, 2026-08-25): the dominant use case is an insurance card or ID — front and
// back — so two is the useful number, and a patient who fires off a dozen photos must not flood the
// thread. This caps the DISPLAY only: every photo is still downloaded and still filed into Open
// Dental imaging. Capping the INGEST instead would silently drop photos out of the patient's chart,
// which is data we can never get back.
export const MAX_RENDERED_MEDIA_PER_MESSAGE = 2;

// Normalize ONE persisted file descriptor into the shape vue-advanced-chat renders.
// Single shared primitive on purpose: the initial-load and live-listener paths in
// store/conversationsV2 used to carry near-identical copies of this mapping, which is exactly how
// divergent media bugs ship (Media-URL Standard, rule 3).
export function normalizeMediaFile(f, apiBase) {
  if (!f) return null;
  const sid = f.mediaSid || f.sid || f.Sid;
  const ext = (function (t, n) {
    try {
      if (typeof t === 'string' && t.includes('/')) return t.split('/')[1];
      if (typeof n === 'string' && n.includes('.')) return n.split('.').pop();
      return t || '';
    } catch (_) { return t || ''; }
  })(f.type || f.contentType, f.name || f.filename);
  const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(String(ext || '').toLowerCase());
  const rawUrl = f.url || f.localUrl || '';
  const cleanUrl = renderMediaUrl(f, apiBase);
  return {
    name: f.name || f.filename || sid,
    size: f.size,
    type: f.type || f.contentType || (isImg ? `image/${ext}` : ext),
    extension: ext,
    mime: f.type || f.contentType || (isImg ? `image/${ext}` : ''),
    url: cleanUrl,
    localUrl: cleanUrl,
    image: isImg,
    s3Key: f.s3Key || f.S3Key || undefined,
    mediaSid: sid || proxyMediaSid(rawUrl),
    ingestFailed: f.ingestFailed === true ? true : undefined,
  };
}

// True when a normalized descriptor can actually be shown (an s3Key to re-sign, or a real URL).
function isServable(f) {
  return !!(f && (f.url || f.s3Key || f.S3Key));
}

/**
 * The message(s) the chat should render for ONE Firestore message doc.
 *
 * vue-advanced-chat 0.6.9 renders a single `message.file` — it has no multi-attachment model — so a
 * multi-photo MMS is fanned out into one bubble per photo (up to MAX_RENDERED_MEDIA_PER_MESSAGE).
 * Before this, the store kept only files[0] and ~22% of prod inbound MMS were quietly showing one
 * photo when the patient had sent two.
 *
 * Mutates `msg` in place (as the store always did) and returns the array to render: `[msg]`, or
 * `[msg, continuation…]`. Continuations are DISPLAY-ONLY clones — suffixed `_id`, no reactions, and
 * flagged `isMediaContinuation` so nothing mistakes one for a real message.
 *
 * Anything beyond the cap is ANNOUNCED, never silently dropped — silently hiding a patient's photo
 * is the exact bug this function exists to fix.
 */
export function expandMediaMessage(msg, apiBase, maxPhotos = MAX_RENDERED_MEDIA_PER_MESSAGE) {
  if (!msg) return [msg];
  const raw = (Array.isArray(msg.files) && msg.files.length) ? msg.files
    : (msg.file ? [msg.file] : (Array.isArray(msg.media) ? msg.media : []));
  const normalized = raw.map((f) => normalizeMediaFile(f, apiBase)).filter(Boolean);
  if (!normalized.length) return [msg];

  const servable = normalized.filter(isServable);

  // Nothing renderable at all → the placeholder path, byte-for-byte as before.
  if (!servable.length) {
    const first = normalized[0];
    msg.file = first;
    msg.files = [first];
    const ph = legacyMediaPlaceholderContent(first, first.url, msg.content);
    if (ph !== null) { msg.file = undefined; msg.files = []; msg.content = ph; }
    return [msg];
  }

  const cap = Math.max(1, maxPhotos);
  const shown = servable.slice(0, cap);
  const hidden = servable.length - shown.length;

  msg.file = shown[0];
  msg.files = [shown[0]];

  const out = [msg];
  for (let i = 1; i < shown.length; i += 1) {
    const baseId = msg._id || msg.id || msg.messageSID || '';
    out.push({
      ...msg,
      _id: `${baseId}#${i}`,
      id: `${baseId}#${i}`,
      file: shown[i],
      files: [shown[i]],
      content: '',        // the caption belongs to the first bubble only
      reactions: undefined, // a reaction is attached to the real message, never to a clone
      isMediaContinuation: true,
    });
  }
  if (hidden > 0) {
    const last = out[out.length - 1];
    last.content = `${last.content ? `${last.content}\n` : ''}+${hidden} more photo${hidden > 1 ? 's' : ''} received.`;
  }
  return out;
}

// True when a message's only attachment is legacy Twilio media — a media SID with no real S3 URL that
// can no longer be served. The UI shows a "no longer available" placeholder and fires no request.
export function isLegacyExpiredMedia(m) {
  if (!m) return false;
  // pickRenderableFile, not files[0]: one unservable item must not condemn a message that also
  // carries a servable one (see pickRenderableFile).
  const f = m.file || pickRenderableFile(m.files) || null;
  const media = Array.isArray(m.media) ? m.media : [];
  const sid =
    (f && (f.mediaSid || f.sid || f.Sid || f.ingestFailed)) ||
    (media[0] && (media[0].sid || media[0].Sid || media[0].mediaSid)) ||
    null;
  if (!sid) return false;                    // no legacy media SID / failed-ingest marker at all
  if (f && realFileUrl(f)) return false;                                   // has a real S3 file URL
  if (media.some((md) => md && md.url && !isProxyUrl(md.url))) return false; // media[] has a real URL
  return true;
}

// Media-URL Standard rule 6: when a message's media can no longer be served — a legacy media SID but
// no real S3/blob URL and no s3Key to re-sign — the chat MUST show a graceful placeholder, never a
// native broken-image icon. vue-advanced-chat renders file.url DIRECTLY, so an empty url paints a
// broken image; we fix it at the DATA layer instead (the dead per-message slot never rendered).
// Returns the placeholder CONTENT to display (caller drops the file so the library's default text
// renderer shows it), or null when the media IS servable (real url or an s3Key to re-sign).
export function legacyMediaPlaceholderContent(file, resolvedUrl, existingContent) {
  if (!file) return null;
  const servable = resolvedUrl || file.s3Key || file.S3Key;
  // `ingestFailed` (2026-07-31) is the in-house counterpart of a legacy sid: an inbound MMS whose
  // S3 re-host failed is persisted with a locator and NO servable url. It is included here because
  // the Twilio MediaSid is not always parseable from the media URL — without this, such a message
  // would fall through with an empty url and vue-advanced-chat would paint a broken image.
  const unservableMarker = file.mediaSid || file.sid || file.Sid || file.ingestFailed;
  if (servable || !unservableMarker) return null;
  const label = file.image ? '📷 Photo no longer available' : '📎 Attachment no longer available';
  const existing = String(existingContent || '').trim();
  // Keep a real caption; replace an auto placeholder like "[Photo] name".
  return existing && !existing.startsWith('[') ? `${existing}\n${label}` : label;
}
